import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { PanelBarItemComponent, PanelBarExpandMode } from '@progress/kendo-angular-layout';
import { BehaviorSubject } from 'rxjs';
import { FormDialogTemplate } from '../form-dialog/form-dialog.template';
import { PanelbarService } from './expandable-panelbar.service';
import { ApprovalStatus } from '../../components/ctwr/workflow.service';
import { UploadComponent, FileInfo, FileState } from '@progress/kendo-angular-upload';

/**
 * @whatItDoes It's a generic template to create an expandable panelBars
 *
 * Each panel expands and shows a form/forms under the panel title. 
 *
 * @howToUse
 * TO DO: Write howToUse when done implementing this component.
 *
 */
@Component({
  selector: 'expandable-panelbar',
  templateUrl: './expandable-panelbar.component.html',
  styleUrls: ['./expandable-panelbar.component.scss']
})
export class ExpandablePanelbarComponent implements OnInit {

  @Input() template: PanelBarTemplate;
  private uidCounter: number = Math.random() * 255 | 0;

  formDialogTemplate: BehaviorSubject<FormDialogTemplate> = new BehaviorSubject<FormDialogTemplate>(null);
  constructor(
    public panelbarService: PanelbarService,
    private formBuilder: FormBuilder,
  ) {

  }

  ngOnInit() { }

  /**
   * Apply class to form control
   * `hidden` if control is not in the editForm
   * `flex-end` if control has align set to right
   * `stretch` if control stretch set to true
   */
  getControlClass = (control: any, form): string => {
    if (!form.get(control.name)) return 'hidden';
    let cssClass = 'item-content';
    if (control.type === 'submit' || control.type === 'action' || control.type === 'button-group') {
      cssClass = control.class ? control.class : '';
    };
    if (control.align === 'right') cssClass += ' flex-end';
    if (control.stretch) cssClass += ' stretch';
    if (control.class) cssClass += ` ${control.class}`;
    return cssClass;
  }

  /**
   * Return existence of control in the edit form
   * Used to dynamically show/hide form controls
   */
  showControl = (control, form) => form.get(control.name);


  /**
   * Update panelbar service expanded state for panelbar
   */
  onChange = (e, item: PanelBarItemComponent) => {
    const pb = e[0];
    pb.expanded ? this.panelbarService.addExpandPanelbar(pb.id) :
      this.panelbarService.removeExpandPanelbar(pb.id);
  }


  getInputClass = (control: any, form: FormGroup) => {
    const ctrl = form.controls[control.name];
    const valid = ctrl.valid ? ' ng-valid' : ' ng-invalid';
    const touched = ctrl.touched ? ' ng-touched' : ' ng-untouched';
    const value = ctrl.value ? ' ng-not-empty' : '';
    const type = control.type === 'person' ? ' search' : '';
    return `modelo-text-input ${valid}${touched}${value}`;
  }


  getTextAreaClass = (control: any, form: FormGroup) => {
    const ctrl = form.controls[control.name ? control.name : control];
    const valid = ctrl.valid ? ' ng-valid' : ' ng-invalid';
    const touched = ctrl.touched ? ' ng-touched' : ' ng-untouched';
    const value = ctrl.value ? ' ng-not-empty' : ' ng-empty';
    return `textarea ${valid}${touched}${value}`;

  }


  getLabelClass = (status: ApprovalStatus): string => {
    const style = ApprovalStatusStyles.find(x => x.value === status);
    return style ? style.cssClass : '';
  }


  isRequired = (c: any, form: FormGroup): boolean => {
    const ctrl = c.name ? form.get(c.name) : null;
    return ctrl ? ctrl.invalid : true;
  }


  getCustomForm(file: any, name: string): FormGroup {
    if (!file.form) file.form = this.formBuilder.group({ comment: ['', Validators.required] });
    return file.form
  }


  onUploadComment = (control: any, file: any) => {
    if (control.comment) {
      file[0].form.reset(file);
    }
  }


  onCommentSave = (control: any, files: any[], form: FormGroup, c: UploadComponent) => {
    files[0].comment = files[0].form.get("comment").value;
    delete files[0].form;
    if (control.save) {
      control.save(files, form, c);
      files[0].state = FileState.Initial;
    }
  }


  getNextUid() {
    this.uidCounter = this.uidCounter + 1;
    return `option${this.uidCounter}`
  }


  getUid(form: FormGroup, index, control: any): string {
    let fc = form.get(optionsName);
    if (!fc) {
      form.addControl(optionsName, new FormControl({}));
      fc = form.get(optionsName);
    }

    if (!fc.value[control.name]) {
      const arr: any[] = [];
      for (let i = 0; i < control.options.length; i++) {
        arr.push(this.getNextUid());
      }
      const obj = fc.value;
      obj[control.name] = arr;
      fc.setValue(obj);
    }

    return (fc.value[control.name] || fc.value)[index];
  }

  getValue = (control: any, form: FormGroup): string => {
    return control.value ? control.value : form.controls[control.name].value
  }

  onCommentCancel = (file: FileInfo, c: UploadComponent) => c.fileList.remove(file.uid);


  isDelegateDisabled = (form: FormGroup, control: any): boolean => {
    return control.disabled ? control.disabled(form) : false;
  }

  isOptionDisabled = (form: FormGroup, option: any): any => {
    if (option.disabled) {
      return option.disabled(form);
    }
    else {
      return null;
    }
  }

  getPanelbarClass = (iconClass: string) => {
    // return iconClass.split(' ')[1] + '-bg';
    return 'secondary-blue-bg';
  }

  isExpanded = (): boolean => {
    return this.panelbarService.expanded.find(x => x === this.template.id) ? true : this.template.expanded;
  }

  getTitle(control, form): string {
    return typeof (control.title) === "string" ? control.title : control.title(form);
  }
}

/*
* A panelsbar is a building block of an Expandable-panelbar. 
*/
export class PanelBarTemplate {
  panelBars: PanelBarTemplate[] = [];
  forms: FormGroup[] = [];
  controls: any[] = [];
  canRefresh: boolean;
  title: string;
  iconStack = true;
  iconClass: string;
  expanded: boolean;
  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  approvalStatus: BehaviorSubject<string> = new BehaviorSubject<string>(undefined)
  id: any;
  expandMode: number = PanelBarExpandMode.Full;
  private timer: any;
  constructor() {
    this.loading.subscribe(x => {
      if (x) {
        this.timer = setTimeout(() => this.loading.next(false), TIMEOUT);
      } else {
        clearTimeout(this.timer);
      }
    });
  }


  /**
   * 
   * @param panelBar 
   * @param isAddToFront 
   */
  protected addPanelBar(panelBar: PanelBarTemplate, isAddToFront = false) {
    isAddToFront ?
      this.panelBars.unshift(panelBar) :
      this.panelBars.push(panelBar);
  }


  /**
   * 
   */
  reset() {
    this.panelBars = [];
  }


  /**
   * 
   */
  refresh = () => null;

}

const colorClasses = {
  SUCCESS: 'cgp-success',
  WARNING: 'secondary-orange',
  DANGER: 'cgp-red',
  DEFAULT: 'secondary-blue'
}

const ApprovalStatusStyles = [
  {
    value: ApprovalStatus.PENDING,
    cssClass: colorClasses.DEFAULT,
  },
  {
    value: ApprovalStatus.DES_COMPLETE,
    cssClass: colorClasses.DEFAULT,
  },
  {
    value: ApprovalStatus.CANCELLED,
    cssClass: colorClasses.WARNING,
  },
  {
    value: ApprovalStatus.WAIVED,
    cssClass: colorClasses.WARNING,
  },
  {
    value: ApprovalStatus.ACCEPTED,
    cssClass: colorClasses.SUCCESS,
  }, {
    value: ApprovalStatus.COMPLETE,
    cssClass: colorClasses.SUCCESS,
  },
  {
    value: ApprovalStatus.FINISHED,
    cssClass: colorClasses.SUCCESS,
  },
  {
    value: ApprovalStatus.APPROVED,
    cssClass: colorClasses.SUCCESS,
  },
  {
    value: ApprovalStatus.REJECTED,
    cssClass: colorClasses.DANGER,
  },
  {
    value: ApprovalStatus.REJECT,
    cssClass: colorClasses.DANGER,
  },
]

const optionsName = 'optionIds';

const TIMEOUT = 30000;
